import { useState } from 'react'
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl'

import { CheckIcon } from '@gto/icons-react/outline/check'
import { PencilIcon } from '@gto/icons-react/outline/pencil'
import { Badge } from '@gto/ui/badge'
import { Button } from '@gto/ui/button'
import { cn } from '@gto/ui/cn'
import { IconButton } from '@gto/ui/icon-button'
import { Paper } from '@gto/ui/paper'
import type { TypographyColor } from '@gto/ui/typography'
import { Typography } from '@gto/ui/typography'

import type { AffiliateAdminPayoutSchema } from '@/api/gen'
import { useAdjustPayoutAmount } from '@/api/gen'
import { AppDialog } from '@/components/app-dialog'
import { TextareaField, TextField } from '@/components/form'
import { MutationError } from '@/components/mutation-error'
import { PayoutDetailsView } from '@/components/payout-details-view'
import { StatusBadge } from '@/components/status-badge'
import {
  type MutationErrorState,
  useMutationErrorHandler,
} from '@/hooks/use-mutation-error-handler'

const PAYOUT_AMOUNT_COLOR: Record<string, TypographyColor> = {
  PAID: 'primary',
  REQUESTED: 'primary',
  REJECTED: 'text.disabled',
  CANCELLED: 'text.disabled',
  REVERSED: 'text.disabled',
}

const PAYOUT_AMOUNT_DECORATION: Record<string, string | undefined> = {
  REJECTED: 'line-through',
  CANCELLED: 'line-through',
  REVERSED: 'line-through',
}

type ConfirmAction = 'approve' | 'reject' | 'reverse' | 'cancel'

// Reject and Cancel are one click apart on the same footer and move money in
// opposite directions (forfeit vs. return), so they must never read as the same
// kind of action: Reject is the only REQUESTED action styled destructive-red,
// Cancel is amber, Approve is green.
const CONFIRM_STYLE: Record<
  ConfirmAction,
  { panel: string; button: 'primary' | 'error' | 'warning' }
> = {
  approve: { panel: 'border border-success-main/[0.3] bg-success-main/[0.08]', button: 'primary' },
  reject: { panel: 'border border-error-main/[0.3] bg-error-main/[0.08]', button: 'error' },
  reverse: { panel: 'border border-error-main/[0.3] bg-error-main/[0.08]', button: 'error' },
  cancel: { panel: 'border border-warning-main/[0.3] bg-warning-main/[0.08]', button: 'warning' },
}

const CONFIRM_YES_MESSAGE_ID: Record<ConfirmAction, string> = {
  approve: 'admin.payouts.confirmYesApprove',
  reject: 'admin.payouts.confirmYesReject',
  reverse: 'admin.payouts.confirmYesReverse',
  cancel: 'admin.payouts.confirmYesCancel',
}

type PayoutDetailDialogProps = {
  payout: AffiliateAdminPayoutSchema | null
  onClose: () => void
  onApprove: (payout: AffiliateAdminPayoutSchema) => void
  /**
   * Reject a REQUESTED payout (→ REJECTED). TERMINAL: the partner forfeits the
   * balance — the money does NOT come back. This is the ban/fraud path, not the
   * "called it off" path; that one is {@link onCancel}.
   */
  onReject: (payout: AffiliateAdminPayoutSchema) => void
  /**
   * Cancel a REQUESTED payout (→ CANCELLED), returning the money to the
   * partner's Open Balance so they can request it again.
   */
  onCancel: (payout: AffiliateAdminPayoutSchema) => void
  /**
   * Reverse a PAID payout back to the partner's Open Balance (money bounced from
   * PayPal/Wise). `reason` is required by the backend (422 on blank); the confirm
   * step gates the button on a non-empty reason before this fires.
   */
  onReverse: (payout: AffiliateAdminPayoutSchema, reason: string) => void
  onAmountAdjusted: (updated: AffiliateAdminPayoutSchema) => void
  loading: boolean
  /**
   * Approve/reject mutation error state from {@link useMutationErrorHandler}.
   * Drives the inline {@link MutationError} and is reset when a fresh confirm
   * prompt opens so a stale error doesn't bleed across attempts. Optional —
   * when absent, no error UI renders (e.g. callers that haven't wired it).
   */
  actionError?: MutationErrorState
}

export const PayoutDetailDialog = ({
  payout,
  onClose,
  onApprove,
  onReject,
  onCancel,
  onReverse,
  onAmountAdjusted,
  loading,
  actionError,
}: PayoutDetailDialogProps) => {
  const { formatMessage } = useIntl()
  const [adjustedAmount, setAdjustedAmount] = useState('')
  const [editingAmount, setEditingAmount] = useState(false)
  const [confirmAction, setConfirmAction] = useState<ConfirmAction | null>(null)
  const [reverseReason, setReverseReason] = useState('')

  // Money-critical: a failed amount adjust must reach Sentry AND surface to the
  // operator inside the dialog, and must NOT fire the success side-effect
  // (close the editor + invalidate the list). Approve/reject failures already
  // route through `actionError` from the parent; adjust is a distinct mutation
  // owned by this dialog, so it has its own sibling handler with its own
  // user-facing message.
  const adjustError = useMutationErrorHandler('useAdjustPayoutAmount failed: ')

  const adjustMutation = useAdjustPayoutAmount({
    mutation: {
      onMutate: adjustError.clearError,
      onSuccess: (updated: AffiliateAdminPayoutSchema) => {
        setEditingAmount(false)
        setAdjustedAmount('')
        onAmountAdjusted(updated)
      },
      onError: adjustError.onError,
    },
  })

  if (payout == null) {
    return null
  }

  const details = payout.payout_details as Record<string, string>
  const isRequested = payout.status === 'REQUESTED'
  const isPaid = payout.status === 'PAID'
  const isReversed = payout.status === 'REVERSED'
  const displayAmount = Number(payout.requested_amount)
  const wasAdjusted = payout.original_amount != null

  const handleSaveAmount = () => {
    const newAmount = Number(adjustedAmount)
    if (!newAmount || newAmount <= 0) {
      return
    }
    adjustMutation.mutate({ payoutId: payout.id, data: { requested_amount: newAmount } })
  }

  const handleCancelEdit = () => {
    adjustError.clearError()
    setAdjustedAmount('')
    setEditingAmount(false)
  }

  const openConfirm = (action: ConfirmAction) => {
    actionError?.clearError()
    setReverseReason('')
    setConfirmAction(action)
  }

  const closeConfirm = () => {
    setConfirmAction(null)
    setReverseReason('')
  }

  // No close here: success unmounts the dialog anyway, so the only path that would
  // observe it is failure — where closing wipes the reason the operator typed before
  // they have even read the error.
  const handleConfirm = () => {
    switch (confirmAction) {
      case 'approve':
        return onApprove(payout)
      case 'reject':
        return onReject(payout)
      case 'cancel':
        return onCancel(payout)
      case 'reverse':
        return onReverse(payout, reverseReason.trim())
      case null:
        return
    }
  }

  const confirmPrompt = (action: ConfirmAction): string => {
    const partner = payout.partner.display_name
    const amount = displayAmount.toFixed(2)
    switch (action) {
      case 'approve':
        return formatMessage({ id: 'admin.payouts.confirmApprove' }, { amount, partner })
      case 'reject':
        return formatMessage({ id: 'admin.payouts.confirmRejectForfeit' }, { amount, partner })
      case 'cancel':
        return formatMessage({ id: 'admin.payouts.confirmCancel' }, { amount, partner })
      case 'reverse':
        return formatMessage({ id: 'admin.payouts.confirmReverse' }, { partner })
    }
  }

  return (
    <AppDialog
      open
      onClose={onClose}
    >
      <AppDialog.Header
        subtitle={payout.partner.email}
        title={payout.partner.display_name}
      />

      <AppDialog.Body>
        <div className="flex flex-col items-center gap-1.5">
          <StatusBadge status={payout.status} />

          {editingAmount ? (
            <>
              <div className="flex items-center gap-2">
                <TextField
                  autoFocus
                  className="w-[180px]"
                  min={0}
                  placeholder={displayAmount.toFixed(2)}
                  step={0.01}
                  type="number"
                  value={adjustedAmount}
                  onChange={(e) => setAdjustedAmount(e.target.value)}
                />
                <IconButton
                  className="p-2"
                  color="primary"
                  data-tst="payout-adjust-save"
                  disabled={adjustMutation.isPending}
                  size="small"
                  onClick={handleSaveAmount}
                >
                  <CheckIcon
                    height={16}
                    width={16}
                  />
                </IconButton>
                <Button
                  disabled={adjustMutation.isPending}
                  size="small"
                  onClick={handleCancelEdit}
                >
                  <FormattedMessage id="admin.common.cancel" />
                </Button>
              </div>
              <MutationError
                data-tst="payout-adjust-error"
                messageId="admin.payouts.adjustError"
                show={adjustError.hasError}
              />
            </>
          ) : (
            <div className="flex flex-col items-center gap-0.5">
              <Typography
                color={PAYOUT_AMOUNT_COLOR[payout.status] ?? 'text.primary'}
                style={{ textDecoration: PAYOUT_AMOUNT_DECORATION[payout.status] }}
                variant="h4"
              >
                <FormattedNumber
                  currency="USD"
                  style="currency"
                  value={displayAmount}
                />
              </Typography>
              {wasAdjusted && (
                <Typography
                  color="text.secondary"
                  variant="caption"
                >
                  <FormattedMessage id="admin.payouts.originalAmount" />:{' '}
                  <FormattedNumber
                    currency="USD"
                    style="currency"
                    value={Number(payout.original_amount)}
                  />
                </Typography>
              )}
            </div>
          )}

          {isRequested && !editingAmount && (
            <Button
              size="small"
              variant="outlined"
              startIcon={
                <PencilIcon
                  height={14}
                  width={14}
                />
              }
              onClick={() => {
                setAdjustedAmount(displayAmount.toFixed(2))
                setEditingAmount(true)
              }}
            >
              <FormattedMessage id="admin.payouts.adjustAmount" />
            </Button>
          )}

          <Typography
            color="text.secondary"
            variant="caption"
          >
            <FormattedMessage id="admin.payouts.requestedDate" />:{' '}
            {new Date(payout.created_at).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            })}
          </Typography>
        </div>

        <Paper
          className="border border-divider bg-text-primary/[0.03] p-4"
          elevation="0"
        >
          <div className="flex items-center gap-2">
            <Typography
              color="text.secondary"
              variant="caption"
            >
              <FormattedMessage id="admin.payouts.method" />
            </Typography>
            <Badge color="secondary">{payout.payout_method ?? '—'}</Badge>
          </div>
          <PayoutDetailsView
            details={details}
            method={payout.payout_method ?? ''}
          />
        </Paper>

        {payout.resolved_at != null && (
          <div className="flex gap-8">
            <div className="flex flex-col gap-0.5">
              <Typography
                color="text.secondary"
                variant="caption"
              >
                <FormattedMessage id="admin.payouts.resolvedDate" />
              </Typography>
              <Typography variant="body2">
                {new Date(payout.resolved_at).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </Typography>
            </div>
            <div className="flex flex-col gap-0.5">
              <Typography
                color="text.secondary"
                variant="caption"
              >
                <FormattedMessage id="admin.payouts.resolvedBy" />
              </Typography>
              <Typography variant="body2">{payout.resolved_by_email ?? '—'}</Typography>
            </div>
          </div>
        )}

        {isReversed && (
          <Paper
            className="border border-error-main/[0.3] bg-error-main/[0.06] p-4"
            data-tst="payout-reversal-info"
            elevation="0"
          >
            <div className="flex gap-8">
              <div className="flex flex-col gap-0.5">
                <Typography
                  color="text.secondary"
                  variant="caption"
                >
                  <FormattedMessage id="admin.payouts.reversedDate" />
                </Typography>
                <Typography variant="body2">
                  {payout.reversed_at == null
                    ? '—'
                    : new Date(payout.reversed_at).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                </Typography>
              </div>
              <div className="flex flex-col gap-0.5">
                <Typography
                  color="text.secondary"
                  variant="caption"
                >
                  <FormattedMessage id="admin.payouts.reversedBy" />
                </Typography>
                <Typography variant="body2">{payout.reversed_by_email ?? '—'}</Typography>
              </div>
            </div>
            {payout.reversal_reason != null && payout.reversal_reason !== '' && (
              <div className="mt-3 flex flex-col gap-0.5">
                <Typography
                  color="text.secondary"
                  variant="caption"
                >
                  <FormattedMessage id="admin.payouts.reversalReason" />
                </Typography>
                <Typography variant="body2">{payout.reversal_reason}</Typography>
              </div>
            )}
          </Paper>
        )}

        {confirmAction && (
          <Paper
            className={cn('p-4', CONFIRM_STYLE[confirmAction].panel)}
            data-tst={`payout-confirm-${confirmAction}`}
            elevation="0"
          >
            <Typography
              className="text-center"
              variant="body2"
            >
              {confirmPrompt(confirmAction)}
            </Typography>
            {confirmAction === 'reverse' && (
              <TextareaField
                autoFocus
                required
                className="mt-3"
                data-tst="payout-reverse-reason"
                label={formatMessage({ id: 'admin.payouts.reverseReason' })}
                maxLength={500}
                rows={2}
                value={reverseReason}
                onChange={(e) => setReverseReason(e.target.value)}
              />
            )}
            <div className="mt-2 flex justify-center gap-2">
              <Button
                color={CONFIRM_STYLE[confirmAction].button}
                disabled={loading || (confirmAction === 'reverse' && reverseReason.trim() === '')}
                size="small"
                variant="contained"
                onClick={handleConfirm}
              >
                {formatMessage({ id: CONFIRM_YES_MESSAGE_ID[confirmAction] })}
              </Button>
              <Button
                disabled={loading}
                size="small"
                onClick={closeConfirm}
              >
                <FormattedMessage id="admin.common.cancel" />
              </Button>
            </div>
          </Paper>
        )}

        <MutationError
          data-tst="payout-action-error"
          detail={actionError?.detail}
          messageId="admin.payouts.actionError"
          show={actionError?.hasError ?? false}
        />
      </AppDialog.Body>

      {!confirmAction && isRequested && (
        <AppDialog.Footer>
          <Button
            color="warning"
            data-tst="payout-cancel"
            disabled={loading}
            variant="outlined"
            onClick={() => openConfirm('cancel')}
          >
            <FormattedMessage id="admin.payouts.cancelPayout" />
          </Button>
          <Button
            color="error"
            data-tst="payout-reject"
            disabled={loading}
            variant="outlined"
            onClick={() => openConfirm('reject')}
          >
            <FormattedMessage id="admin.payouts.reject" />
          </Button>
          <Button
            color="primary"
            disabled={loading}
            variant="contained"
            onClick={() => openConfirm('approve')}
          >
            <FormattedMessage id="admin.payouts.markAsPaid" />
          </Button>
        </AppDialog.Footer>
      )}

      {!confirmAction && isPaid && (
        <AppDialog.Footer>
          <Button
            color="error"
            data-tst="payout-reverse"
            disabled={loading}
            variant="outlined"
            onClick={() => openConfirm('reverse')}
          >
            <FormattedMessage id="admin.payouts.reverse" />
          </Button>
        </AppDialog.Footer>
      )}
    </AppDialog>
  )
}
